import { Box, IconButton, Modal, ModalDialog, Typography } from "@mui/joy";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Gig } from "../../entities/gig";
import { setFormOpen, setSelectedGigIDs } from "../../reducers/rhapsody";
import { ServiceCard } from "../servicesList/serviceCard";
import { ServiceViewer } from "../servicesList/serviceViewer";
import { ResolvedService } from "./useInsights";

/**
 * Drill-down dialog listing the services related to a clicked insight item
 * (an organization, venue, session type or month). Reuses the same service
 * cards as the services list; clicking a card opens its form (own services)
 * or its invite page (services from an organization).
 *
 * @param root0 props
 * @param root0.open whether the dialog is visible
 * @param root0.title heading (e.g. the organization or venue name)
 * @param root0.subtitle short summary under the title
 * @param root0.items the related, resolved services
 * @param root0.onClose closes the dialog
 * @returns {ReactElement} ServicesDialog
 */
export function ServicesDialog({
  open,
  title,
  subtitle,
  items,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  items: ResolvedService[];
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const [viewer, setViewer] = useState<{ url: string; title?: string } | null>(
    null
  );

  // Most recent first, grouped by month so the period is always clear.
  const sorted = useMemo(
    () =>
      [...items].sort((a, b) =>
        b.gig.dateFromUTC.localeCompare(a.gig.dateFromUTC)
      ),
    [items]
  );

  const groups = useMemo(() => {
    const out: { label: string; items: ResolvedService[] }[] = [];
    sorted.forEach((item) => {
      const label = item.gig.toBeDetermined()
        ? "Date to be determined"
        : item.gig.from(item.venue?.tzName).format("MMMM YYYY");
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(item);
      else out.push({ label, items: [item] });
    });
    return out;
  }, [sorted]);

  const onGigClick = (gig: Gig) => {
    if (gig.sourceID === 420) {
      dispatch(
        setSelectedGigIDs({
          id: gig.id,
          sourceRecordID: gig.sourceRecordID,
          sourceID: gig.sourceID,
        })
      );
      dispatch(setFormOpen({ isOpen: true, formID: "gig" }));
      onClose();
    } else {
      const url = gig.getInviteURL();
      if (url) setViewer({ url, title: gig.title ?? "Service" });
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog
          layout="center"
          sx={{
            p: 0,
            borderRadius: "20px",
            border: "none",
            width: "min(720px, calc(100vw - 24px))",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow:
              "0 12px 24px rgba(0, 0, 0, 0.12), 0 32px 80px rgba(0, 0, 0, 0.28)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 3,
              pt: 2.5,
              pb: 1.5,
              flexShrink: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                level="h5"
                sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
                noWrap
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  level="body2"
                  sx={{ color: "var(--mui-palette-text-secondary)" }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              size="sm"
              variant="soft"
              color="neutral"
              onClick={onClose}
              sx={{ borderRadius: "50%", flexShrink: 0 }}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: 14 }}></i>
            </IconButton>
          </Box>

          <Box
            sx={{
              px: 3,
              pb: 3,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              flex: 1,
              minHeight: 0,
            }}
          >
            {groups.length ? (
              groups.map((group) => (
                <Box
                  key={group.label}
                  sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                  <Typography
                    level="body3"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8125rem",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      py: 0.5,
                      background: "var(--mui-palette-background-surface, #fff)",
                    }}
                  >
                    {group.label}
                  </Typography>
                  {group.items.map((item, i) => (
                    <ServiceCard
                      key={item.gig.id}
                      index={i}
                      gig={item.gig}
                      venue={item.venue}
                      contractor={item.contractor}
                      project={item.project}
                      stage={item.stage}
                      onClick={onGigClick}
                    />
                  ))}
                </Box>
              ))
            ) : (
              <Typography
                level="body2"
                sx={{
                  color: "var(--mui-palette-text-tertiary)",
                  textAlign: "center",
                  py: 4,
                }}
              >
                No services to show.
              </Typography>
            )}
          </Box>
        </ModalDialog>
      </Modal>
      <ServiceViewer
        url={viewer?.url ?? null}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
    </>
  );
}
